/**
 * 内置 Agent 定义
 * 为多 Agent 协作系统提供默认的 Agent 配置
 */

import { Agent } from './agent-manager';

/**
 * 内置的默认 Agent
 */
export const BUILTIN_AGENTS: Agent[] = [
  {
    id: 'agent-frontend',
    name: '前端开发工程师',
    description: '专注于用户界面和交互体验的开发',
    avatar: '👨‍💻',
    category: 'development',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'ui-development',
        name: 'UI 开发',
        description: '开发响应式用户界面',
        required: true
      },
      {
        id: 'state-management',
        name: '状态管理',
        description: '管理应用状态和数据流',
        required: true
      },
      {
        id: 'api-integration',
        name: 'API 集成',
        description: '集成后端 API',
        required: true
      }
    ],
    tools: [
      { id: 'react', name: 'React', type: 'framework' },
      { id: 'typescript', name: 'TypeScript', type: 'language' },
      { id: 'tailwind', name: 'Tailwind CSS', type: 'styling' }
    ],
    prompts: {
      system: '你是一位经验丰富的前端开发工程师，擅长使用 React 和 TypeScript 开发现代化的 Web 应用。',
      thinking: '思考用户体验、性能优化和代码可维护性。',
      output: '提供清晰的代码实现，包含必要的注释和类型定义。'
    },
    taskTypes: ['ui', 'frontend', 'component', 'style'],
    collaboration: {
      worksWith: ['agent-backend', 'agent-designer'],
      provides: ['ui-components', 'user-interactions'],
      requires: ['api-endpoints', 'design-specs']
    }
  },
  
  {
    id: 'agent-backend',
    name: '后端开发工程师',
    description: '专注于服务端逻辑和数据处理',
    avatar: '🔧',
    category: 'development',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'api-design',
        name: 'API 设计',
        description: '设计 RESTful 或 GraphQL API',
        required: true
      },
      {
        id: 'database-design',
        name: '数据库设计',
        description: '设计和优化数据库结构',
        required: true
      },
      {
        id: 'security',
        name: '安全实现',
        description: '实现认证和授权机制',
        required: true
      }
    ],
    tools: [
      { id: 'nodejs', name: 'Node.js', type: 'runtime' },
      { id: 'express', name: 'Express', type: 'framework' },
      { id: 'postgresql', name: 'PostgreSQL', type: 'database' }
    ],
    prompts: {
      system: '你是一位经验丰富的后端开发工程师，擅长设计可扩展的 API 和数据库架构。',
      thinking: '考虑性能、安全性、可扩展性和数据一致性。',
      output: '提供完整的 API 实现，包含错误处理和数据验证。'
    },
    taskTypes: ['api', 'backend', 'database', 'auth'],
    collaboration: {
      worksWith: ['agent-frontend', 'agent-dba'],
      provides: ['api-endpoints', 'data-models'],
      requires: ['ui-requirements', 'business-logic']
    }
  },
  
  {
    id: 'agent-architect',
    name: '架构师',
    description: '负责系统架构设计和技术决策',
    avatar: '🏗️',
    category: 'architecture',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'system-design',
        name: '系统设计',
        description: '设计整体系统架构',
        required: true
      },
      {
        id: 'tech-selection',
        name: '技术选型',
        description: '选择合适的技术栈',
        required: true
      },
      {
        id: 'performance-optimization',
        name: '性能优化',
        description: '识别和解决性能瓶颈',
        required: true
      }
    ],
    tools: [
      { id: 'drawio', name: 'Draw.io', type: 'diagram' },
      { id: 'benchmark', name: '性能测试工具', type: 'testing' }
    ],
    prompts: {
      system: '你是一位资深的系统架构师，擅长设计高可用、可扩展的分布式系统。',
      thinking: '从整体架构、技术选型、性能、安全、成本等多个维度思考。',
      output: '提供清晰的架构图和详细的技术方案说明。'
    },
    taskTypes: ['architecture', 'design', 'optimization', 'scaling'],
    collaboration: {
      worksWith: ['agent-frontend', 'agent-backend', 'agent-devops'],
      provides: ['architecture-design', 'tech-specs'],
      requires: ['business-requirements', 'constraints']
    }
  },
  
  {
    id: 'agent-tester',
    name: '测试工程师',
    description: '负责质量保证和测试',
    avatar: '🧪',
    category: 'quality',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'test-planning',
        name: '测试计划',
        description: '制定全面的测试策略',
        required: true
      },
      {
        id: 'test-automation',
        name: '测试自动化',
        description: '编写自动化测试脚本',
        required: true
      },
      {
        id: 'bug-tracking',
        name: '缺陷跟踪',
        description: '发现和跟踪软件缺陷',
        required: true
      }
    ],
    tools: [
      { id: 'jest', name: 'Jest', type: 'testing' },
      { id: 'cypress', name: 'Cypress', type: 'e2e' },
      { id: 'playwright', name: 'Playwright', type: 'automation' }
    ],
    prompts: {
      system: '你是一位专业的测试工程师，擅长设计全面的测试方案和编写高质量的测试代码。',
      thinking: '考虑各种边界情况、异常场景和用户体验。',
      output: '提供详细的测试用例、测试代码和测试报告。'
    },
    taskTypes: ['test', 'qa', 'validation', 'verification'],
    collaboration: {
      worksWith: ['agent-frontend', 'agent-backend'],
      provides: ['test-reports', 'bug-reports'],
      requires: ['feature-specs', 'api-docs']
    }
  },
  
  {
    id: 'agent-pm',
    name: '产品经理',
    description: '负责产品规划和需求定义',
    avatar: '📊',
    category: 'product',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'requirement-analysis',
        name: '需求分析',
        description: '分析和定义产品需求',
        required: true
      },
      {
        id: 'user-research',
        name: '用户研究',
        description: '进行用户调研和分析',
        required: true
      },
      {
        id: 'roadmap-planning',
        name: '路线图规划',
        description: '制定产品发展路线图',
        required: true
      }
    ],
    tools: [
      { id: 'figma', name: 'Figma', type: 'design' },
      { id: 'jira', name: 'JIRA', type: 'management' }
    ],
    prompts: {
      system: '你是一位经验丰富的产品经理，擅长理解用户需求并转化为清晰的产品方案。',
      thinking: '从用户价值、商业价值、技术可行性等角度思考。',
      output: '提供清晰的需求文档，包含用户故事、验收标准和优先级。'
    },
    taskTypes: ['requirement', 'planning', 'analysis', 'research'],
    collaboration: {
      worksWith: ['agent-designer', 'agent-architect'],
      provides: ['requirements', 'user-stories'],
      requires: ['market-research', 'user-feedback']
    }
  },
  
  {
    id: 'agent-reviewer',
    name: '代码审查专家',
    description: '专注于代码质量和最佳实践',
    avatar: '🔍',
    category: 'quality',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'code-review',
        name: '代码审查',
        description: '审查代码质量和规范',
        required: true
      },
      {
        id: 'security-audit',
        name: '安全审计',
        description: '识别安全漏洞',
        required: true
      },
      {
        id: 'performance-review',
        name: '性能评估',
        description: '评估代码性能',
        required: true
      }
    ],
    tools: [
      { id: 'eslint', name: 'ESLint', type: 'linting' },
      { id: 'sonarqube', name: 'SonarQube', type: 'analysis' }
    ],
    prompts: {
      system: '你是一位严谨的代码审查专家，擅长发现代码中的问题并提供改进建议。',
      thinking: '从代码质量、性能、安全性、可维护性等方面进行全面审查。',
      output: '提供详细的审查报告，包含问题说明、严重程度和改进建议。'
    },
    taskTypes: ['review', 'audit', 'inspection'],
    collaboration: {
      worksWith: ['agent-frontend', 'agent-backend'],
      provides: ['review-reports', 'improvement-suggestions'],
      requires: ['source-code', 'pr-description']
    }
  },
  
  {
    id: 'agent-devops',
    name: 'DevOps 工程师',
    description: '负责部署和运维',
    avatar: '🚀',
    category: 'operations',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'ci-cd',
        name: 'CI/CD',
        description: '配置持续集成和部署',
        required: true
      },
      {
        id: 'infrastructure',
        name: '基础设施',
        description: '管理云基础设施',
        required: true
      },
      {
        id: 'monitoring',
        name: '监控告警',
        description: '设置监控和告警',
        required: true
      }
    ],
    tools: [
      { id: 'docker', name: 'Docker', type: 'container' },
      { id: 'kubernetes', name: 'Kubernetes', type: 'orchestration' },
      { id: 'terraform', name: 'Terraform', type: 'infrastructure' }
    ],
    prompts: {
      system: '你是一位专业的 DevOps 工程师，擅长构建可靠的部署流程和基础设施。',
      thinking: '考虑可用性、可扩展性、安全性和成本优化。',
      output: '提供完整的部署配置和运维文档。'
    },
    taskTypes: ['deployment', 'infrastructure', 'monitoring', 'automation'],
    collaboration: {
      worksWith: ['agent-backend', 'agent-architect'],
      provides: ['deployment-configs', 'infrastructure-specs'],
      requires: ['application-specs', 'resource-requirements']
    }
  },
  
  {
    id: 'agent-writer',
    name: '技术文档专家',
    description: '负责编写技术文档',
    avatar: '📝',
    category: 'documentation',
    status: 'active',
    version: '1.0.0',
    capabilities: [
      {
        id: 'api-documentation',
        name: 'API 文档',
        description: '编写 API 文档',
        required: true
      },
      {
        id: 'user-guide',
        name: '用户指南',
        description: '编写用户使用指南',
        required: true
      },
      {
        id: 'technical-blog',
        name: '技术博客',
        description: '撰写技术文章',
        required: false
      }
    ],
    tools: [
      { id: 'markdown', name: 'Markdown', type: 'markup' },
      { id: 'swagger', name: 'Swagger', type: 'api-doc' }
    ],
    prompts: {
      system: '你是一位专业的技术文档编写专家，擅长将复杂的技术概念转化为清晰易懂的文档。',
      thinking: '考虑目标读者、文档结构、示例代码和最佳实践。',
      output: '提供结构清晰、内容完整的技术文档。'
    },
    taskTypes: ['documentation', 'guide', 'tutorial', 'readme'],
    collaboration: {
      worksWith: ['agent-frontend', 'agent-backend', 'agent-architect'],
      provides: ['documentation', 'guides'],
      requires: ['feature-implementation', 'api-specs']
    }
  }
];

/**
 * 获取所有内置 Agent
 */
export function getBuiltinAgents(): Agent[] {
  return BUILTIN_AGENTS;
}

/**
 * 根据 ID 获取内置 Agent
 */
export function getBuiltinAgent(agentId: string): Agent | undefined {
  return BUILTIN_AGENTS.find(agent => agent.id === agentId);
}

/**
 * 根据类别获取 Agent
 */
export function getAgentsByCategory(category: string): Agent[] {
  return BUILTIN_AGENTS.filter(agent => agent.category === category);
}

/**
 * 根据能力获取 Agent
 */
export function getAgentsByCapability(capabilityId: string): Agent[] {
  return BUILTIN_AGENTS.filter(agent =>
    agent.capabilities.some(cap => cap.id === capabilityId)
  );
}